import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import AvailabilityPanel from "../../components/director/AvailabilityPanel";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import DirectorPage from "../../components/director/page/DirectorPage";
import { delinquencyAnswer } from "../../components/director/page/answers";
import readingWarnings from "../../components/director/page/readingWarnings";
import { useDirectorActions, useDirectorContext } from "@contexts/director";
import {
  compactCents,
  formatBps,
  formatCents,
  formatCivilDate,
  formatCompetenceShort,
  formatDateTimeCompact,
} from "../../utils/directorFormat";
import { companyQuery, currentCompetence, newestExtractedAt } from "./module1Utils";
import { primaryScreen } from "./complementaryPages";

const SCREEN = primaryScreen("delinquency");

const PAGE_SIZE = 25;

export const BUCKET_LABELS = {
  "1_30": "1 a 30 dias",
  "31_60": "31 a 60 dias",
  "61_90": "61 a 90 dias",
  "91_180": "91 a 180 dias",
  "180_plus": "Mais de 180 dias",
};

const BUCKET_ORDER = Object.keys(BUCKET_LABELS);

// Filtro por faixa (clique na barra ou no cartão "Mais de 90 dias"). O BE filtra a lista por
// `min_days` (a partir do início da faixa); a faixa exata se completa aqui, na página carregada.
export const BAND_FILTERS = {
  "1_30": { minDays: 1, label: "1 a 30 dias", buckets: ["1_30"] },
  "31_60": { minDays: 31, label: "31 a 60 dias", buckets: ["31_60"] },
  "61_90": { minDays: 61, label: "61 a 90 dias", buckets: ["61_90"] },
  "91_180": { minDays: 91, label: "91 a 180 dias", buckets: ["91_180"] },
  "180_plus": { minDays: 181, label: "mais de 180 dias", buckets: ["180_plus"] },
  over90: { minDays: 91, label: "mais de 90 dias", buckets: ["91_180", "180_plus"] },
};
const HIDDEN_STATES = new Set(["restricted", "unavailable", "empty"]);
const NOTICE_STATES = new Set(["restricted", "unavailable", "empty", "stale", "partial"]);

const count = (value) => (Number.isSafeInteger(value) ? value.toLocaleString("pt-BR") : "—");

// Barras horizontais; com `onSelect`, cada barra é um botão que filtra a lista (clique de novo
// tira o filtro).
const HorizontalBars = ({ items, ariaLabel, selected = null, onSelect }) => {
  const maximum = Math.max(1, ...items.map((item) => (Number.isSafeInteger(item.value) ? Math.abs(item.value) : 0)));
  return <ul className="director-hbars" aria-label={ariaLabel}>
    {items.map((item) => {
      const content = <>
        <span className="director-hbars__label">{item.label}</span>
        <span className="director-hbars__track" aria-hidden="true">
          <i style={{ "--bar-size": `${Number.isSafeInteger(item.value) && item.value > 0 ? Math.max(2, Math.round((item.value * 100) / maximum)) : 0}%` }} />
        </span>
        <strong className="director-hbars__value" title={Number.isSafeInteger(item.value) ? formatCents(item.value) : undefined}>{compactCents(item.value)}</strong>
        {item.note && <small className="director-hbars__note">{item.note}</small>}
      </>;
      const clickable = onSelect && item.selectable !== false;
      return <li key={item.id} data-tone={item.tone} className={clickable ? "director-hbars__item--button" : undefined}>
        {clickable
          ? <button
            type="button"
            className="director-hbars__button"
            aria-pressed={selected === item.id ? "true" : "false"}
            onClick={() => onSelect(selected === item.id ? null : item.id)}
          >{content}</button>
          : content}
      </li>;
    })}
  </ul>;
};

const Delinquency = () => {
  const { selectedCompanyId, accessByCompany = {}, filters = {}, refreshStatus = null } = useDirectorContext();
  const actions = useDirectorActions();
  const access = accessByCompany[selectedCompanyId] || {};
  const projectKey = filters.projectKey || null;
  const [band, setBand] = useState(null);
  const [pageState, setPageState] = useState({ projectKey, band, page: 1 });
  const page = pageState.projectKey === projectKey && pageState.band === band ? pageState.page : 1;
  const bandFilter = band ? BAND_FILTERS[band] : null;
  const query = useMemo(() => ({
    projectKey,
    ...(bandFilter ? { minDays: bandFilter.minDays } : {}),
    page,
    pageSize: PAGE_SIZE,
  }), [page, projectKey, bandFilter]);
  const surface = useDirectorSurface("delinquency", query);
  const { data } = surface;
  const summary = data?.summary || {};
  const state = data?.state || null;
  const showNumbers = data && !HIDDEN_STATES.has(state);
  const buckets = BUCKET_ORDER.map((key) => {
    const bucket = (data?.buckets || []).find((item) => item.key === key) || {};
    return {
      id: key,
      label: BUCKET_LABELS[key],
      value: bucket.overdueCents ?? 0,
      tone: key,
      note: Number.isSafeInteger(bucket.customers) ? `${count(bucket.customers)} cliente${bucket.customers === 1 ? "" : "s"}` : null,
    };
  });
  const projects = [...(data?.projects || [])]
    .sort((left, right) => (right.overdueCents ?? 0) - (left.overdueCents ?? 0))
    .map((project) => ({
      id: project.projectKey || "__without_project__",
      label: project.projectKey ? (project.name || "Obra sem nome") : "Sem obra",
      value: project.overdueCents,
      tone: "project",
      selectable: Boolean(project.projectKey),
    }));
  const items = (data?.items || []).filter((item) => !bandFilter || bandFilter.buckets.includes(item.bucket));
  const pagination = data?.pagination || null;
  const totalPages = pagination ? Math.max(1, Math.ceil((pagination.total || 0) / (pagination.pageSize || PAGE_SIZE))) : 1;
  const freshness = Array.isArray(data?.freshness)
    ? newestExtractedAt(data.freshness)
    : (data?.freshness?.extractedAt || null);
  const goTo = (target) => setPageState({ projectKey, band, page: target });
  const selectProject = (key) => actions?.setFilters({ legalEntityId: null, projectKey: key, costCenterId: null });
  const positionDate = data?.referenceDate || data?.asOf;
  const projectName = (data?.projects || []).find((project) => project.projectKey === projectKey)?.name;

  return <DirectorPage
    title={SCREEN.label}
    question={SCREEN.question}
    eyebrow={`Diretoria · ${access.companyName || "portfólio"}${positionDate ? ` · Posição em ${formatCivilDate(String(positionDate).slice(0, 10))}` : ""}`}
    onPublished={surface.reload}
    qualitySources={[surface]}
    status={<>
      <ModuleState {...surface} onRetry={surface.reload} />
      {data && NOTICE_STATES.has(state) && <AvailabilityPanel state={state} reasonCode={data.reasonCode} />}
    </>}
    warnings={showNumbers ? readingWarnings({ refreshStatus, access }) : []}
    answer={showNumbers ? delinquencyAnswer(data, { partialScope: access.completePortfolioScope === false }) : null}
    cardsLabel="Indicadores de inadimplência"
    cards={showNumbers ? [
      {
        key: "overdue",
        label: "Vencido e não pago",
        cents: summary.overdueCents,
        note: `${formatBps(summary.overdueShareBps)} do que vence nos próximos 12 meses`,
        tone: "pressure",
      },
      {
        key: "customers",
        label: "Clientes em atraso",
        value: count(summary.customersOverdue),
        note: `de ${count(summary.customersTotal)} clientes com parcelas`,
        tone: "warning",
      },
      {
        key: "over90",
        label: "Mais de 90 dias",
        cents: summary.over90Cents,
        note: `${count(summary.over90Customers)} cliente${summary.over90Customers === 1 ? "" : "s"} · ver na lista`,
        tone: "pressure",
        pressed: band === "over90",
        onClick: () => setBand((current) => (current === "over90" ? null : "over90")),
      },
      {
        key: "recovered",
        label: "Recuperado no mês",
        cents: summary.recoveredThisMonthCents,
        note: `Parcelas vencidas pagas em ${formatCompetenceShort(currentCompetence())}`,
        tone: "good",
      },
    ] : []}
    chart={showNumbers ? <section className="director-module1__split director-module1__split--even">
      <article className="director-module1__panel">
        <header>
          <div><span className="director-eyebrow">Por tempo de atraso</span><h2>Quanto mais antigo, mais difícil de receber</h2></div>
          <p>Clique numa faixa para filtrar a lista.</p>
        </header>
        <HorizontalBars items={buckets} ariaLabel="Vencido por faixa de atraso" selected={band} onSelect={setBand} />
      </article>
      <article className="director-module1__panel">
        <header>
          <div><span className="director-eyebrow">Por obra</span><h2>Onde o atraso se concentra</h2></div>
          <p>Clique numa obra para filtrar a tela.</p>
        </header>
        {projects.length
          ? <HorizontalBars items={projects} ariaLabel="Vencido por obra" selected={projectKey} onSelect={selectProject} />
          : <p className="director-owner-footnote">Nenhuma obra com parcela vencida.</p>}
      </article>
    </section> : null}
    detail={showNumbers ? <article className="director-module1__panel">
      <header>
        <div>
          <span className="director-eyebrow">Clientes com parcela vencida</span>
          <h2>Ordenado pelo valor vencido · {count(pagination?.total ?? items.length)} clientes</h2>
        </div>
      </header>
      {(bandFilter || projectKey) && <div className="director-owner-switches" aria-label="Filtros da lista">
        {bandFilter && <span className="director-filter-chip">
          {`Faixa: ${bandFilter.label}`}
          <button type="button" aria-label="Tirar o filtro de faixa" onClick={() => setBand(null)}>×</button>
        </span>}
        {projectKey && <span className="director-filter-chip">
          {`Obra: ${projectName || "obra escolhida"}`}
          <button type="button" aria-label="Tirar o filtro de obra" onClick={() => selectProject(null)}>×</button>
        </span>}
      </div>}
      <div className="director-table-scroll">
        <table className="director-table director-receivables-table director-delinquency-table" aria-label="Clientes com parcela vencida">
          <thead>
            <tr>
              <th scope="col">Cliente</th>
              <th scope="col">Obra</th>
              <th scope="col">Unidade</th>
              <th scope="col">Parcelas vencidas</th>
              <th scope="col">Valor vencido</th>
              <th scope="col">Atraso mais antigo</th>
              <th scope="col">Última baixa</th>
              <th scope="col">Faixa</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => <tr key={`${item.customerRef}-${item.unitRef || "sem-unidade"}`}>
              <th scope="row">
                <Link
                  aria-label={`Abrir ${item.displayName || "cliente"}`}
                  to={`/dashboard/director/customer-receipts/${encodeURIComponent(item.customerRef)}${companyQuery(selectedCompanyId)}`}
                >
                  {item.displayName || "Cliente sem nome disponível"}
                </Link>
              </th>
              <td>{item.projectName || (item.projectKey ? "Obra sem nome" : "Sem obra")}</td>
              <td>{item.unitDisplayName || "—"}</td>
              <td>{count(item.overdueInstallments)}</td>
              <td>{formatCents(item.overdueCents)}</td>
              <td>{Number.isSafeInteger(item.daysOverdue) ? `${item.daysOverdue} dias` : "—"}</td>
              <td>{item.lastSettlementDate ? formatCivilDate(String(item.lastSettlementDate).slice(0, 10)) : "Sem baixa"}</td>
              <td><span className="director-bucket-chip" data-bucket={item.bucket}>{BUCKET_LABELS[item.bucket] || "—"}</span></td>
            </tr>)}
          </tbody>
        </table>
      </div>
      {pagination && totalPages > 1 && <nav aria-label="Paginação de clientes em atraso" className="director-pagination">
        <button aria-label="Página anterior" disabled={page <= 1} onClick={() => goTo(page - 1)} type="button">Anterior</button>
        <span>Página {page} de {totalPages}</span>
        <button aria-label="Próxima página" disabled={page >= totalPages} onClick={() => goTo(page + 1)} type="button">Próxima</button>
      </nav>}
      <p className="director-owner-footnote">
        O cliente abre o detalhe com todas as parcelas da unidade. A cobrança continua sendo feita no ERP ou pela equipe do cliente.
      </p>
    </article> : null}
    notes={showNumbers ? <>
      <p>Valores nominais, das parcelas a receber do ERP, sem correção.</p>
      {positionDate && <p>{`Dias de atraso e faixas contados até ${formatCivilDate(String(positionDate).slice(0, 10))}.`}</p>}
      {bandFilter && <p>O filtro por faixa pede ao servidor os clientes a partir do início da faixa; a contagem total da lista considera esse corte.</p>}
      {freshness && <p>Fontes atualizadas em {formatDateTimeCompact(freshness)}.</p>}
    </> : null}
  />;
};

export default Delinquency;
